import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  /** The bubble content shown on hover/focus. */
  content: ReactNode;
  /** The trigger element the tooltip is anchored to. */
  children: ReactNode;
  /** Max width of the bubble in px. Defaults to 260. */
  width?: number;
  /** Class applied to the inline trigger wrapper. */
  className?: string;
  /** When true, the tooltip never opens. */
  disabled?: boolean;
  /** Bubble colour scheme. Defaults to the cream "light" bubble. */
  tone?: "light" | "dark";
}

const GAP = 10; // space between trigger and bubble
const MARGIN = 8; // min distance from the viewport edge

type Placement = "top" | "bottom";

/**
 * Hover/focus tooltip rendered in a portal so it is never clipped by a
 * scrollable parent (e.g. the comparison modal or plan-card overflow).
 * Prefers showing above the trigger with a downward caret, flips below
 * when there isn't enough room, and clamps horizontally to the viewport.
 */
const Tooltip = ({
  content,
  children,
  width = 260,
  className = "",
  disabled = false,
  tone = "light",
}: TooltipProps) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    caretLeft: 0,
    placement: "top" as Placement,
  });

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;

    const t = trigger.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    const triggerCenter = t.left + t.width / 2;

    // Prefer above the trigger; flip below if it would overflow the top.
    let placement: Placement = "top";
    let top = t.top - GAP - b.height;
    if (top < MARGIN) {
      placement = "bottom";
      top = t.bottom + GAP;
    }

    // Centre horizontally on the trigger, clamped to the viewport.
    let left = triggerCenter - b.width / 2;
    left = Math.max(
      MARGIN,
      Math.min(left, window.innerWidth - b.width - MARGIN)
    );

    setCoords({ top, left, caretLeft: triggerCenter - left, placement });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  const show = () => !disabled && setOpen(true);
  const hide = () => setOpen(false);

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        tabIndex={0}
      >
        {children}
      </span>

      {open &&
        content &&
        createPortal(
          <div
            ref={bubbleRef}
            role="tooltip"
            className="pointer-events-none"
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              maxWidth: width,
              zIndex: 2000,
            }}
          >
            <div
              className={`relative rounded-xl px-4 py-3 text-[13px] leading-[1.5] shadow-[0_4px_16px_rgba(16,24,40,0.12)] ${
                tone === "dark"
                  ? "bg-[#1D2939] text-white"
                  : "bg-[#FFF5E6] text-[#344054]"
              }`}
            >
              {content}
              <span
                style={{ left: coords.caretLeft }}
                className={`absolute h-3 w-3 -translate-x-1/2 rotate-45 ${
                  tone === "dark" ? "bg-[#1D2939]" : "bg-[#FFF5E6]"
                } ${coords.placement === "top" ? "-bottom-1.5" : "-top-1.5"}`}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export { Tooltip };
